import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { ChakraTextareaField, MessageBox } from 'app/components/Core';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { dynamicYupResolver } from 'app/utils/utils';
import { PlaylistAudio } from 'types/playlists.types';
import { useAppDispatch, useAppSelector } from 'app/store';
import { getUpdatePlaylistAudioLink } from 'app/store/Playlists/selectors';
import { useFetchValidationSchema } from 'app/utils/hooks';
import { transformSchemaToYup } from 'app/utils/schemaToYup';
import { updatePlaylistAudioPropsEffect } from 'app/store/Playlists/effects';
import { noteFormErrorMessages } from 'app/components/Playlist/NoteDialog/constants';

interface NoteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onCloseComplete: () => void;
  playlistAudio: PlaylistAudio | null;
}

export const NoteDialog = ({
  isOpen,
  playlistAudio,
  onClose,
  onCloseComplete,
}: NoteDialogProps) => {
  const dispatch = useAppDispatch();
  const updatePlaylistAudioLink = useAppSelector(getUpdatePlaylistAudioLink);

  const defaultAudioNoteValues = {
    note: playlistAudio?.note || '',
  };

  const { validationSchema } = useFetchValidationSchema({
    link: updatePlaylistAudioLink,
  });

  const yupValidationSchema = useMemo(
    () =>
      validationSchema &&
      transformSchemaToYup(
        validationSchema,
        defaultAudioNoteValues,
        noteFormErrorMessages
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [validationSchema]
  );

  const {
    handleSubmit,
    register,
    reset,
    formState: { isSubmitting, isValid, isDirty, errors },
  } = useForm<{ note: string }>({
    defaultValues: defaultAudioNoteValues,
    mode: 'all',
    context: {
      yupValidationSchema,
    },
    resolver: dynamicYupResolver,
  });

  const handleClose = () => {
    onClose();
    reset();
  };

  const onSubmit = async ({ note }) => {
    try {
      if (playlistAudio) {
        await dispatch(
          updatePlaylistAudioPropsEffect({
            playlistAudioGuid: playlistAudio.guid,
            note,
          })
        ).unwrap();
      }
      handleClose();
    } catch (e) {
      // Continue
    }
  };

  useEffect(() => {
    reset({ note: playlistAudio?.note || '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playlistAudio, reset]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      onCloseComplete={onCloseComplete}
      data-testid="note-dialog"
      size="md"
    >
      <ModalOverlay />
      <ModalContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader
            data-testid="dialog-title"
            title={playlistAudio?.audio.title}
          >
            {playlistAudio?.audio.title}
          </ModalHeader>
          <ModalBody data-testid="dialog-content">
            <ChakraTextareaField
              {...register('note')}
              errorMessage={errors.note?.message}
              isInvalid={Boolean(errors.note)}
              label="Note"
              formControlStyleProps={{
                h: '180px',
              }}
              h="180px"
              resize="none"
              data-testid="note-input"
            />
            <MessageBox
              boxProps={{
                mt: 3,
                w: 'full',
                'data-testid': 'info-message',
              }}
            >
              Any note you add will be visible to the person viewing the share
              link.
            </MessageBox>
          </ModalBody>
          <ModalFooter data-testid="dialog-actions">
            <Button
              variant="secondary"
              data-testid="cancel-button"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              isDisabled={isSubmitting || !isValid || !isDirty}
              data-testid="save-button"
              type="submit"
              ml={2}
            >
              Save
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};
